/**
 * Product card quantity stepper.
 *
 * A Shopify cart holds VARIANT lines, not products, so the stepper on a card
 * is always bound to one variant — never to "the product". Two rules decide
 * which one:
 *
 *   1. The card knows its variant (single-variant product, or a card whose
 *      options are all rendered as swatches): bind to it. When the shopper
 *      switches swatch, the binding follows, so a variant that isn't in the
 *      cart correctly falls back to the plain "+" button.
 *   2. The card does NOT know a variant (its options live behind the
 *      choose-options drawer): bind to the product's cart line only when
 *      there is exactly one. With two or more variants of the same product in
 *      the cart there is no honest number to show and no unambiguous line to
 *      decrement, so the card stays a "+" that reopens the drawer.
 *
 * Adding is left entirely to the existing add-to-cart form (xProductCart);
 * this component only owns the increment/decrement/remove of a line that is
 * already in the cart.
 */
if (!window.Eurus.loadedScript.has('card-qty-stepper.js')) {
  window.Eurus.loadedScript.add('card-qty-stepper.js');

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      Alpine.store('xCardQty', {
        // variantId -> { key, quantity }. `key` (not `line`) is what we send
        // to cart/change.js: line indexes shift whenever another line is
        // removed, keys don't.
        lines: {},
        // productId -> [variantId, ...] — feeds rule 2 above.
        variantsByProduct: {},
        loaded: false,
        _pending: null,

        init() {
          this.refresh();
          document.addEventListener('eurus:cart:items-changed', () => { this.refresh(); });
          // Safari/Chrome mobile restore a cached page without re-running the
          // request, so a cart edited on another page would leave every card
          // showing a stale count.
          window.addEventListener('pageshow', (event) => { if (event.persisted) this.refresh(); });
        },

        refresh() {
          if (this._pending) return this._pending;
          this._pending = fetch(`${Shopify.routes.root}cart.js`, {
            headers: { Accept: 'application/json' }
          })
            .then((response) => response.json())
            .then((cart) => { this._index(cart); })
            .catch((error) => { console.error('Error:', error); })
            .finally(() => { this._pending = null; });

          return this._pending;
        },

        _index(cart) {
          const lines = {};
          const variantsByProduct = {};

          (cart.items || []).forEach((item) => {
            // The same variant can occupy several lines when line item
            // properties differ (engraving, gift wrap...). The card can only
            // address one of them, so it owns the first and reports that
            // line's quantity — the cart page stays the place to manage the
            // rest.
            if (lines[item.variant_id]) return;

            lines[item.variant_id] = { key: item.key, quantity: item.quantity };
            (variantsByProduct[item.product_id] = variantsByProduct[item.product_id] || [])
              .push(item.variant_id);
          });

          this.lines = lines;
          this.variantsByProduct = variantsByProduct;
          this.loaded = true;
        },

        quantityOf(variantId) {
          const line = variantId ? this.lines[variantId] : null;

          return line ? line.quantity : 0;
        },

        async setQuantity(variantId, quantity) {
          const line = variantId ? this.lines[variantId] : null;
          if (!line) return;

          await Alpine.store('xCartHelper').waitForCartUpdate();
          window.updatingCart = true;

          try {
            const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                id: line.key,
                quantity: quantity,
                sections: Alpine.store('xCartHelper').getSectionsToRender().map((section) => section.id),
                sections_url: window.location.pathname
              })
            });
            const cart = await response.json();

            // 422 = out of stock past the requested quantity, 404 = the line
            // vanished under us. Either way the cart is the source of truth.
            if (cart.status) {
              await this.refresh();
              return;
            }

            this._index(cart);
            Alpine.store('xCartHelper').reRenderSections(cart.sections);
            Alpine.store('xCartHelper').currentItemCount = cart.item_count;
          } catch (error) {
            console.error('Error:', error);
            await this.refresh();
          } finally {
            window.updatingCart = false;
          }

          document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
        }
      });

      Alpine.data('xCardQtyStepper', (
        productId,
        cardVariantId,
        variantEventKey
      ) => ({
        variantId: cardVariantId ? String(cardVariantId) : '',
        busy: false,

        init() {
          if (!variantEventKey) return;

          // Swatch cards swap their variant client-side (variant-select-card.js),
          // and the hidden form input is updated imperatively — outside Alpine's
          // reactivity — so we follow the event instead of watching the input.
          document.addEventListener(`eurus:product-card-variant-select:updated:${variantEventKey}`, (event) => {
            const variant = event.detail && event.detail.currentVariant;
            this.variantId = variant && variant.id ? String(variant.id) : '';
          });
        },

        get boundVariantId() {
          if (this.variantId) return this.variantId;

          const variants = Alpine.store('xCardQty').variantsByProduct[productId] || [];

          return variants.length === 1 ? String(variants[0]) : '';
        },

        get qty() {
          return Alpine.store('xCardQty').quantityOf(this.boundVariantId);
        },

        async _apply(quantity) {
          if (this.busy) return;
          this.busy = true;

          try {
            await Alpine.store('xCardQty').setQuantity(this.boundVariantId, quantity);
          } finally {
            this.busy = false;
          }
        },

        increase() { return this._apply(this.qty + 1); },
        decrease() { return this._apply(Math.max(this.qty - 1, 0)); },
        remove() { return this._apply(0); }
      }));
    });
  });
}
